import { searchAllSpaces, browseAllSpaces, AllSpaceSearchParams, AllSpaceSearchItem } from './search-api-client';
import Redux from '../../redux/ReduxConnector';
import LocalStorage, { LocalStorageKey } from '../../utils/LocalStorage';

export type SearchFilterKey =
  | 'id'
  | 'postcode'
  | 'prefecture'
  | 'city'
  | 'address'
  | 'parentName'
  | 'buildingName'
  | 'attributes'
  | 'status'
  | 'customerKanaName'
  | 'customerPhoneNumber'
  | 'sumamoruData.sumamoruLinkable'
  | 'sumamoruData.sumamoruProjectTypes'
  | 'sumamoruData.contracts.applicationStatus'
  | 'sumamoruData.contracts.sumamoruStatus';

export type SearchFilters = Partial<Record<SearchFilterKey, string[]>>;

export interface SearchCondition {
  filters: SearchFilters;
  excludeFilters?: SearchFilters;
  length?: number;
  offset?: number;
}

export interface SearchResult {
  hits: AllSpaceSearchItem[];
  nbHits: number;
}

export default class SearchApiIndex {
  private readonly name: string = 'search-api-allSpace';

  private getAccessToken = (): string => {
    return Redux.getStore().user.accessToken || LocalStorage.get(LocalStorageKey.ACCESS_TOKEN) || '';
  };

  private static readonly FILTER_MAPPING: ReadonlyArray<{
    filterKey: string;
    apiParam: keyof AllSpaceSearchParams;
  }> = [
    { filterKey: 'id', apiParam: 'id' },
    { filterKey: 'prefecture', apiParam: 'prefecture' },
    { filterKey: 'attributes', apiParam: 'attributes' },
    { filterKey: 'status', apiParam: 'status' },
    {
      filterKey: 'sumamoruData.sumamoruProjectTypes',
      apiParam: 'sumamoruProjectTypes'
    },
    {
      filterKey: 'sumamoruData.contracts.applicationStatus',
      apiParam: 'contractApplicationStatus'
    },
    {
      filterKey: 'sumamoruData.contracts.sumamoruStatus',
      apiParam: 'contractSumamoruStatus'
    }
  ];

  private static applyArrayFilters(
    filters: SearchFilters,
    params: AllSpaceSearchParams,
    mapping: ReadonlyArray<{
      filterKey: string;
      apiParam: keyof AllSpaceSearchParams;
    }>
  ): void {
    for (const { filterKey, apiParam } of mapping) {
      const values = filters[filterKey];
      if (values && values.length > 0) {
        (params as Record<string, unknown>)[apiParam] = values;
      }
    }
  }

  private convertConditionToParams = (condition: SearchCondition): AllSpaceSearchParams => {
    const params: AllSpaceSearchParams = {};

    if (condition.length !== undefined) {
      params.limit = condition.length;
    }

    if (condition.filters) {
      SearchApiIndex.applyArrayFilters(condition.filters, params, SearchApiIndex.FILTER_MAPPING);

      const linkable = condition.filters['sumamoruData.sumamoruLinkable'];
      if (linkable && linkable.length > 0) {
        params.sumamoruLinkable = linkable[0] === 'true' ? 'true' : 'false';
      }

      const postcode = condition.filters['postcode'];
      if (postcode && postcode.length > 0) {
        params.postcode = postcode[0];
      }

      const city = condition.filters['city'];
      if (city && city.length > 0) {
        params.city = city[0];
      }

      const address = condition.filters['address'];
      if (address && address.length > 0) {
        params.address = address[0];
      }

      const buildingName = condition.filters['buildingName'];
      if (buildingName && buildingName.length > 0) {
        params.buildingName = buildingName[0];
      }

      const customerKanaName = condition.filters['customerKanaName'];
      if (customerKanaName && customerKanaName.length > 0) {
        params.customerKanaName = customerKanaName[0];
      }

      const customerPhoneNumber = condition.filters['customerPhoneNumber'];
      if (customerPhoneNumber && customerPhoneNumber.length > 0) {
        params.customerPhoneNumber = customerPhoneNumber[0];
      }
    }

    if (condition.excludeFilters) {
      const excluded = condition.excludeFilters['sumamoruData.contracts.sumamoruStatus'];
      if (excluded && excluded.length > 0) {
        params.contractSumamoruStatusNot = excluded;
      }
    }

    return params;
  };

  public getName = () => this.name;

  public clearCache = () => {};

  public search = async (condition: SearchCondition): Promise<SearchResult> => {
    const accessToken = this.getAccessToken();
    const params = this.convertConditionToParams(condition);

    const response = await searchAllSpaces(accessToken, params);

    return {
      hits: response.results,
      nbHits: response.results.length
    };
  };

  public getAllHitsByBrowseResponse = async (condition: SearchCondition): Promise<AllSpaceSearchItem[]> => {
    const accessToken = this.getAccessToken();
    const params = this.convertConditionToParams(condition);

    const allResults = await browseAllSpaces(accessToken, params);
    return allResults;
  };
}
